import { Component, Input, OnDestroy, OnInit, TemplateRef } from '@angular/core';
import { Router } from '@angular/router';
import { NbDialogService } from '@nebular/theme';
import * as Highcharts from 'highcharts';
import { Subscription } from 'rxjs';
import { LayoutService } from '../../../../@core/utils';

export interface ChartAlert {
  title: string;
  message: string;
  status: 'basic' | 'primary' | 'success' | 'info' | 'warning' | 'danger' | 'control';
}

@Component({
  template: '',
  styleUrls: [],
})
export abstract class BaseChartComponent implements OnInit, OnDestroy {

  @Input() public showExportButton: boolean = true;
  @Input() public showExpandButton: boolean = true;
  @Input() public allowZoom: boolean = false;
  @Input() public backgroundColor: string = '#ffffff';
  @Input() public boxShadow: boolean = true;
  @Input() public marginBottom: boolean = true;

  public Highcharts = Highcharts;
  public options: Highcharts.Options = this.getOptions();
  public visible: boolean = true;
  public loading: boolean = true;
  public expanded: boolean = false;
  public zoomEnabled: boolean = false;
  public alert: ChartAlert;
  public displayNoData: boolean = false;

  protected instance: Highcharts.Chart;
  private layoutSubscription: Subscription;

  constructor(
    protected dialogService: NbDialogService,
    protected layoutService: LayoutService,
    protected router: Router,
  ) { }

  public ngOnInit(): void {
    this.layoutSubscription = this.layoutService.onChangeLayoutSize().subscribe(() => this.reflow());

    // Reflow graph when printing
    window.matchMedia('print').addEventListener('change', () => this.reflow());
  }

  public ngOnDestroy(): void {
    this.layoutSubscription.unsubscribe();
  }

  public saveInstance(chartInstance: Highcharts.Chart) {
    this.instance = chartInstance;
    this.bindBaseEvents();
  }

  public displayMessage(alertDialog: TemplateRef<any>): void {
    this.dialogService.open(alertDialog);
  }

  protected abstract update(): void;

  protected getOptions(): Highcharts.Options {
    return {
      chart: this.getChartOptions(),
      title: {
        style: {
          fontSize: '1.5rem',
          color: '#4b4b4b',
          fontWeight: 'normal',
        },
        text: '',
      },
      credits: {
        enabled: false,
      },
      exporting: {
        fallbackToExportServer: false,
        type: 'image/png',
        sourceWidth: 1280,
        sourceHeight: 720,
      },
      legend: {
        itemHiddenStyle: {
            color: 'lightgray',
            'text-decoration': 'none'
        }
      },
      navigation: {
        buttonOptions: {
          enabled: false,
        },
      },
      xAxis: {
        labels: {
          style: {
            fontFamily: '"Roboto", sans-serif',
            fontWeight: 'normal',
            fontSize: '0.9375rem',
            color: '#4b4b4b',
          },
        },
      },
    };
  }

  protected getChartOptions(): Highcharts.ChartOptions {
    return {
      backgroundColor: this.backgroundColor,
      style: {
        fontFamily: '"Roboto", sans-serif',
        fontWeight: 'normal',
        fontSize: '0.9375rem',
        color: '#4b4b4b',
      },
      ...(this.allowZoom ? {
        zoomType: 'x',
        resetZoomButton: {
          theme: {
            display: 'none',
          },
        },
      } : {}),
    };
  }

  protected export(): void {
    if (this.instance) {
      this.instance.exportChartLocal({}, {});
    }
  }

  protected expand(expandedDialog: TemplateRef<any>): void {
    this.dialogService.open(expandedDialog);
  }

  protected resetZoom(): void {
    this.instance.zoomOut();
    this.zoomEnabled = false;
  }

  protected reflow(): void {
    if (this.instance && this.instance.options) {
      this.instance.reflow();
    }
  }

  private bindBaseEvents(): void {
    if (this.allowZoom && this.instance?.xAxis?.[0]) {
      Highcharts.addEvent(this.instance.xAxis[0], 'setExtremes', () => {
        this.zoomEnabled = true;
      });
    }
  }

}
